import React, { useState } from 'react';
import { Navigate, Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LayoutDashboard, ShoppingBag, Tag, Users, Settings, LogOut, Menu, Package, TrendingUp, ShoppingCart, MessageSquare } from 'lucide-react';

const Layout = () => {
    const { user, logout, loading } = useAuth();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    if (loading) return null;
    if (!user) return <Navigate to="/login" replace />;

    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
    const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

    const navItems = [
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
        { name: 'Orders', path: '/orders', icon: ShoppingCart },
        { name: 'Categories', path: '/categories', icon: ShoppingBag },
        { name: 'Attributes', path: '/attributes', icon: Tag },
        { name: 'Products', path: '/products', icon: Package },
        { name: 'Customers', path: '/customers', icon: Users },
        { name: 'Ad-Spends', path: '/ad-spends', icon: TrendingUp },
        { name: 'Enquiries', path: '/enquiries', icon: MessageSquare },
        { name: 'Users', path: '/users', icon: Users },
        { name: 'Settings', path: '/settings', icon: Settings },
    ].filter(item => {
        if (item.path === '/ad-spends') {
            return user?.role?.name === 'super_admin';
        }
        return true;
    });

    const SidebarContent = () => (
        <div className="sidebar-inner">
            <div className="sidebar-header">
                <h2>Paradis Admin</h2>
            </div>
            <nav className="sidebar-nav">
                {navItems.map((item) => (
                    <Link
                        key={item.path}
                        to={item.path}
                        className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                    >
                        <item.icon size={20} />
                        <span className="nav-text">{item.name}</span>
                    </Link>
                ))}
            </nav>
            <div className="sidebar-footer">
                <button onClick={logout} className="logout-btn">
                    <LogOut size={20} />
                    <span className="nav-text">Logout</span>
                </button>
            </div>
        </div>
    );

    return (
        <div className={`admin-layout ${!isSidebarOpen ? 'sidebar-collapsed' : ''}`}>
            {/* Desktop Sidebar */}
            <aside className="desktop-sidebar">
                <SidebarContent />
            </aside>

            {/* Mobile/Tablet Menu Overlay */}
            {isMobileMenuOpen && (
                <div className="mobile-overlay" onClick={toggleMobileMenu}></div>
            )}

            {/* Mobile/Tablet Sidebar */}
            <aside className={`mobile-sidebar ${isMobileMenuOpen ? 'open' : ''}`}>
                <SidebarContent />
            </aside>

            <div className="main-wrapper">
                <header className="admin-header">
                    <button className="toggle-btn" onClick={window.innerWidth < 1024 ? toggleMobileMenu : toggleSidebar}>
                        <Menu size={24} />
                    </button>
                    <div className="header-user">
                        <span>Welcome, <strong>{user.name || 'Admin'}</strong></span>
                    </div>
                </header>
                <main className="admin-content">
                    <Outlet />
                </main>
            </div>

            <style>{`
                .admin-layout {
                    display: flex;
                    min-height: 100vh;
                }

                .desktop-sidebar {
                    width: var(--sidebar-width);
                    background: var(--sidebar-bg);
                    color: var(--sidebar-text);
                    transition: width 0.3s ease;
                    flex-shrink: 0;
                    display: flex;
                    flex-direction: column;
                }

                .sidebar-collapsed .desktop-sidebar {
                    width: var(--sidebar-collapsed-width);
                }

                .sidebar-inner {
                    display: flex;
                    flex-direction: column;
                    height: 100vh;
                    position: sticky;
                    top: 0;
                }

                .sidebar-header {
                    height: var(--header-height);
                    display: flex;
                    align-items: center;
                    padding: 0 1.5rem;
                    border-bottom: 1px solid var(--sidebar-active);
                    overflow: hidden;
                    white-space: nowrap;
                }

                .sidebar-header h2 {
                    font-size: 1.25rem;
                    color: white;
                }

                .sidebar-nav {
                    flex: 1;
                    padding: 1rem 0.75rem;
                }

                .nav-link {
                    display: flex;
                    align-items: center;
                    padding: 0.75rem 0.75rem;
                    color: var(--sidebar-text);
                    text-decoration: none;
                    border-radius: 0.5rem;
                    margin-bottom: 0.25rem;
                    transition: all 0.2s;
                    overflow: hidden;
                    white-space: nowrap;
                }

                .nav-link:hover, .nav-link.active {
                    background: var(--sidebar-active);
                    color: white;
                }

                .nav-text {
                    margin-left: 0.75rem;
                    transition: opacity 0.3s;
                }

                .sidebar-collapsed .nav-text, .sidebar-collapsed .sidebar-header h2 {
                    opacity: 0;
                    width: 0;
                }

                .sidebar-footer {
                    padding: 1rem;
                    border-top: 1px solid var(--sidebar-active);
                }

                .logout-btn {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    padding: 0.75rem;
                    background: transparent;
                    border: none;
                    color: var(--sidebar-text);
                    cursor: pointer;
                    border-radius: 0.5rem;
                    text-align: left;
                }

                .logout-btn:hover {
                    background: #991b1b;
                    color: white;
                }

                .main-wrapper {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    min-width: 0;
                }

                .admin-header {
                    height: var(--header-height);
                    background: white;
                    border-bottom: 1px solid var(--border-color);
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 0 1.5rem;
                    position: sticky;
                    top: 0;
                    z-index: 10;
                }

                .toggle-btn {
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: var(--text-main);
                    padding: 0.5rem;
                }

                .admin-content {
                    padding: 1.5rem;
                    flex: 1;
                    overflow-x: auto;
                }

                /* Mobile/Tablet Styles */
                .mobile-sidebar {
                    position: fixed;
                    left: -100%;
                    top: 0;
                    bottom: 0;
                    width: var(--sidebar-width);
                    background: var(--sidebar-bg);
                    z-index: 1000;
                    transition: left 0.3s ease;
                }

                .mobile-sidebar.open {
                    left: 0;
                }

                .mobile-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0, 0, 0, 0.5);
                    z-index: 999;
                }

                @media (max-width: 1024px) {
                    .desktop-sidebar {
                        display: none;
                    }
                    .admin-layout.sidebar-collapsed .desktop-sidebar {
                        display: none;
                    }
                }

                @media (min-width: 1025px) {
                    .mobile-sidebar, .mobile-overlay {
                        display: none;
                    }
                }
            `}</style>
        </div>
    );
};

export default Layout;
